// Enhanced Image Carousel with Swiper.js
// Selectors for carousel elements
const CAROUSEL_SELECTORS = {
  exitButton: '.image-carousel__exit-btn',
  fullscreenPictures: '.image-carousel__fullscreen-picture',
  thumbnailPictures: '.image-carousel__thumbnail-picture',
  thumbnailContainer: '.image-carousel__thumbnails'
};

// Default Swiper configuration
const SWIPER_CONFIG = {
  loop: false,
  threshold: 5, // Minimum swipe distance to trigger slide change
  centeredSlides: true,
  slidesPerView: 1,
  roundLengths: true, // Fix fractional pixel issues
  spaceBetween: 1,
  simulateTouch: false,
  lazy: true,
  //zoom: true,
  pagination: {
    el: '.image-carousel__counter',
    // type: 'fraction',
  },
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
};

// Thumbnail swiper configuration
const THUMBNAIL_SWIPER_CONFIG = {
  loop: false,
  slidesPerView: 6,
  spaceBetween: 8,
  freeMode: true,
  watchSlidesProgress: true,
  observer: true,
  observeParents: true,
  updateOnImagesReady: true,
  preloadImages: false,
  simulateTouch: true,
  lazy: true,
  breakpoints: {
    320: {
      slidesPerView: 3,
      spaceBetween: 4,
    },
    480: {
      slidesPerView: 4,
      spaceBetween: 6,
    },
    768: {
      slidesPerView: 5,
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 6,
      spaceBetween: 8,
    },
    1440: {
      slidesPerView: 8,
      spaceBetween: 10,
    }
  }
};

// Default carousel options
const DEFAULT_CAROUSEL_OPTIONS = {
  enableKeyboardNavigation: false,
  enableFullscreenMode: true,
  enableAutoplay: false,
  showPagination: false,
  enableThumbnails: true,
};

/**
 * Enhanced Image Carousel Class
 * Provides touch-enabled image carousel with fullscreen capabilities
 */
export class ImageCarousel {
  constructor(carouselElement, userOptions = {}) {
    // Initialize core properties
    this.carouselContainer = typeof carouselElement === 'string'
        ? document.querySelector(carouselElement)
        : carouselElement;

    this.options = Object.assign({}, DEFAULT_CAROUSEL_OPTIONS, userOptions);
    this.swiperInstance = null;
    this.thumbnailSwiperInstance = null;
    this.isFullscreenActive = false;

    // Initialize carousel - order matters for thumbs module
    this.initializeThumbnailCarousel();
    this.initializeCarousel();
    this.setupEventListeners();
  }

  /**
   * Initialize the Swiper carousel with configuration
   */
  initializeCarousel() {
    const swiperConfiguration = Object.assign({}, SWIPER_CONFIG, {
      keyboard: {
        enabled: this.options.enableKeyboardNavigation,
      },
      autoplay: this.options.enableAutoplay ? {
        delay: 5000, // 5 seconds
        disableOnInteraction: true,
      } : false,
      pagination: this.options.showPagination ? {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
      } : SWIPER_CONFIG.pagination,
      thumbs: this.options.enableThumbnails && this.thumbnailSwiperInstance ? {
        swiper: this.thumbnailSwiperInstance,
      } : undefined,
    });

    // Add swiper container class
    this.carouselContainer.classList.add('swiper-container');

    // Initialize Swiper
    this.swiperInstance = new Swiper(this.carouselContainer, swiperConfiguration);

    // Setup slide change handler
    this.swiperInstance.on('slideChange', () => {
      this.updateSlideCounter();
    });

    // Initial counter update
    this.updateSlideCounter();
  }

  /**
   * Initialize the thumbnail Swiper carousel
   */
  initializeThumbnailCarousel() {
    if (!this.options.enableThumbnails) return;

    const thumbnailContainer = document.querySelector(CAROUSEL_SELECTORS.thumbnailContainer);
    if (!thumbnailContainer) return;

    // Add swiper container class
    thumbnailContainer.classList.add('swiper-container');

    // Initialize thumbnail Swiper
    this.thumbnailSwiperInstance = new Swiper(thumbnailContainer, THUMBNAIL_SWIPER_CONFIG);
  }

  /**
   * Update the slide counter display
   */
  updateSlideCounter() {
    const counterElement = this.carouselContainer.querySelector('.image-carousel__counter');
    if (counterElement && this.swiperInstance) {
      const currentSlideIndex = this.swiperInstance.activeIndex + 1;
      const totalSlides = this.swiperInstance.slides.length;
      counterElement.textContent = `${currentSlideIndex} / ${totalSlides}`;
    }
  }



  /**
   * Toggle fullscreen mode
   * @param {boolean} shouldEnterFullscreen - Whether to enter or exit fullscreen
   */
  toggleFullscreenMode(shouldEnterFullscreen = !this.isFullscreenActive) {
    const carouselElement = this.carouselContainer;
    const documentBody = document.body;
    const documentHtml = document.documentElement;

    if (shouldEnterFullscreen && !this.isFullscreenActive) {
      this.enterFullscreenMode(carouselElement, documentBody, documentHtml);
    } else if (!shouldEnterFullscreen && this.isFullscreenActive) {
      this.exitFullscreenMode(carouselElement, documentBody, documentHtml);
    }

    this.refreshCarousel();
  }

  /**
   * Enter fullscreen mode
   */
  enterFullscreenMode(carouselElement, documentBody, documentHtml) {
    // Prevent duplicate fullscreen activation
    if (carouselElement.classList.contains('image-carousel--fullscreen')) {
      return;
    }

    // Switch to high-resolution images
    this.switchToFullscreenImages();

    // Enable keyboard navigation in fullscreen
    if (this.swiperInstance.keyboard) {
      this.swiperInstance.keyboard.enable();
    }

    // Apply fullscreen styles
    carouselElement.classList.add('image-carousel--fullscreen');
    documentBody.classList.add('overflow-hidden', 'fullscreen-backdrop');
    documentHtml.classList.add('overflow-hidden', 'fullscreen-backdrop');

    // Show exit button
    const exitButton = carouselElement.querySelector(CAROUSEL_SELECTORS.exitButton);
    if (exitButton) {
      exitButton.classList.remove('hidden');
    }

    this.isFullscreenActive = true;
  }

  /**
   * Exit fullscreen mode
   */
  exitFullscreenMode(carouselElement, documentBody, documentHtml) {
    // Switch back to thumbnail images
    this.switchToThumbnailImages();

    // Disable keyboard navigation if not permanently enabled
    if (!this.options.enableKeyboardNavigation && this.swiperInstance.keyboard) {
      this.swiperInstance.keyboard.disable();
    }

    // Remove fullscreen styles
    carouselElement.classList.remove('image-carousel--fullscreen');
    documentBody.classList.remove('overflow-hidden', 'fullscreen-backdrop');
    documentHtml.classList.remove('overflow-hidden', 'fullscreen-backdrop');

    // Hide exit button
    const exitButton = carouselElement.querySelector(CAROUSEL_SELECTORS.exitButton);
    if (exitButton) {
      exitButton.classList.add('hidden');
    }

    this.isFullscreenActive = false;
  }

  /**
   * Switch to high-resolution images for fullscreen
   */
  switchToFullscreenImages() {
    const fullscreenPictures = this.carouselContainer.querySelectorAll(CAROUSEL_SELECTORS.fullscreenPictures);
    const thumbnailPictures = this.carouselContainer.querySelectorAll(CAROUSEL_SELECTORS.thumbnailPictures);

    fullscreenPictures.forEach(picture => picture.classList.remove('hidden'));
    thumbnailPictures.forEach(picture => picture.classList.add('hidden'));
  }

  /**
   * Switch back to thumbnail images for normal view
   */
  switchToThumbnailImages() {
    const fullscreenPictures = this.carouselContainer.querySelectorAll(CAROUSEL_SELECTORS.fullscreenPictures);
    const thumbnailPictures = this.carouselContainer.querySelectorAll(CAROUSEL_SELECTORS.thumbnailPictures);

    fullscreenPictures.forEach(picture => picture.classList.add('hidden'));
    thumbnailPictures.forEach(picture => picture.classList.remove('hidden'));
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    if (!this.options.enableFullscreenMode) return;

    this.setupFullscreenToggle();
    this.setupExitButton();
    this.setupKeyboardShortcuts();
  }

  /**
   * Setup fullscreen toggle on slide click
   */
  setupFullscreenToggle() {
    this.carouselContainer.addEventListener('click', (clickEvent) => {
      const activeSlide = clickEvent.target.closest('.swiper-slide-active');

      // Only toggle if clicking on the active slide and not on controls
      if (activeSlide && this.isValidFullscreenTrigger(clickEvent)) {
        this.toggleFullscreenMode(true);
      }
    });
  }

  /**
   * Check if click should trigger fullscreen
   */
  isValidFullscreenTrigger(clickEvent) {
    const excludedElements = [
      '.swiper-button-prev',
      '.swiper-button-next',
      '.image-carousel__exit-btn'
    ];

    return !excludedElements.some(selector =>
        clickEvent.target.closest(selector)
    );
  }

  /**
   * Setup exit button functionality
   */
  setupExitButton() {
    const exitButton = this.carouselContainer.querySelector(CAROUSEL_SELECTORS.exitButton);
    if (exitButton) {
      exitButton.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        this.toggleFullscreenMode(false);
      });
    }
  }

  /**
   * Setup keyboard shortcuts
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keyup', (keyboardEvent) => {
      if (keyboardEvent.key === 'Escape' && this.isFullscreenActive) {
        this.toggleFullscreenMode(false);
      }
    });
  }

  /**
   * Refresh the carousel instance
   */
  refreshCarousel() {
    if (this.swiperInstance) {
      this.swiperInstance.update();

      // Dispatch custom update event
      const updateEvent = new CustomEvent('imageCarousel.updated', {
        detail: {
          swiperInstance: this.swiperInstance,
          carouselContainer: this.carouselContainer,
          isFullscreenActive: this.isFullscreenActive
        }
      });

      this.carouselContainer.dispatchEvent(updateEvent);
    }
  }

  /**
   * Destroy the carousel and clean up event listeners
   */
  destroy() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    if (this.thumbnailSwiperInstance) {
      this.thumbnailSwiperInstance.destroy(true, true);
    }

    // Exit fullscreen if active
    if (this.isFullscreenActive) {
      this.toggleFullscreenMode(false);
    }

    // Remove event listeners by cloning and replacing the element
    const newElement = this.carouselContainer.cloneNode(true);
    this.carouselContainer.parentNode.replaceChild(newElement, this.carouselContainer);
  }
}

// Export for ES6 modules
// Also attach to window for backward compatibility
